"use client";

import { imageApiUrl } from "@/lib/images/urls";

import Image from "next/image";
import Link from "next/link";
import { useMessages } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";

import type { SiteCopy, SiteLanguage } from "@/lib/site-data";
import type {
  ArtistSpotlightRow,
  DiscoverFeedEntry,
  GenreSpotlightRow,
} from "@/lib/data/home/discover";
import { HorizontalShelf } from "@/components/site/horizontal-shelf";
import { withLanguagePrefix } from "@/lib/site-locale";

// Per-page slice size. Each page pulls `PAGE_LIMIT` artists + the same
// number of genres, then zips them — so a page is visually 2×PAGE_LIMIT
// spotlight rows.
const PAGE_LIMIT = 4;

type Props = {
  initialItems: DiscoverFeedEntry[];
  initialCursor: string | null;
  language: SiteLanguage;
};

/**
 * DiscoverFeed — cursor-paginated, algorithmically ranked spotlight
 * stream. As the reader scrolls past a sentinel near the page bottom
 * we fetch the next page from `/api/discover/spotlights`. The server
 * interleaves artist and genre spotlights (A/G/A/G) inside each page.
 * When both ranked pools are exhausted the cursor comes back null and
 * the scroll naturally stops.
 */
export function DiscoverFeed({ initialItems, initialCursor, language }: Props) {
  const copy = useMessages() as SiteCopy;
  const [items, setItems] = useState<DiscoverFeedEntry[]>(initialItems);
  const [cursor, setCursor] = useState<string | null>(initialCursor);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(initialCursor === null);
  // Track cluster ids whose cover image has 404'd. The server-side
  // `hasImage` flag only looks at article.imageUrl/ogImageUrl, but the
  // `/api/images/cluster/[id]` endpoint has broader fallbacks (band
  // Spotify art, etc.) — so we always *try* the image and only show
  // the aurora when the request genuinely failed.
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const markImageFailed = (id: string) =>
    setFailedImages((current) => {
      if (current.has(id)) return current;
      const next = new Set(current);
      next.add(id);
      return next;
    });
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchMore = useCallback(async () => {
    if (loading || exhausted || !cursor) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ cursor, limit: String(PAGE_LIMIT) });
      const response = await fetch(`/api/discover/spotlights?${params.toString()}`, {
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`discover ${response.status}`);
      const payload = (await response.json()) as {
        items: DiscoverFeedEntry[];
        nextCursor: string | null;
      };
      setItems((current) => {
        // Dedup defensively in case two pages overlap on the boundary
        // (shouldn't happen with deterministic ranking, but cheap).
        const seen = new Set(current.map((entry) => `${entry.type}:${entry.id}`));
        const fresh = payload.items.filter(
          (entry) => !seen.has(`${entry.type}:${entry.id}`),
        );
        return [...current, ...fresh];
      });
      setCursor(payload.nextCursor);
      if (!payload.nextCursor) setExhausted(true);
    } catch {
      // Swallow — the next sentinel crossing will retry the fetch.
    } finally {
      setLoading(false);
    }
  }, [cursor, loading, exhausted]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || exhausted || !cursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void fetchMore();
        }
      },
      { rootMargin: "1000px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchMore, cursor, exhausted]);

  return (
    <>
      {items.map((entry) =>
        entry.type === "artist" ? (
          <ArtistSpotlightShelf
            key={`artist-${entry.id}`}
            artist={entry}
            language={language}
            copy={copy}
            failedImages={failedImages}
            onImageFail={markImageFailed}
          />
        ) : (
          <GenreSpotlightShelf
            key={`genre-${entry.id}`}
            genre={entry}
            language={language}
            copy={copy}
            failedImages={failedImages}
            onImageFail={markImageFailed}
          />
        ),
      )}

      {!exhausted ? (
        <div
          ref={sentinelRef}
          className="flex justify-center py-8 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground"
        >
          {loading ? copy.ui.loadingMore : ""}
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------
// Spotlight shelves — moved here from the server component so they can
// be rendered by paged client-side fetches as well as the initial
// server-rendered pass.
// ---------------------------------------------------------------------

function ArtistSpotlightShelf({
  artist,
  language,
  copy,
  failedImages,
  onImageFail,
}: {
  artist: ArtistSpotlightRow;
  language: SiteLanguage;
  copy: SiteCopy;
  failedImages: Set<string>;
  onImageFail: (id: string) => void;
}) {
  return (
    <section className="space-y-4">
      <header className="flex items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="relative inline-block size-14 overflow-hidden rounded-full border border-border bg-muted">
            {artist.imageUrl ? (
              <Image src={artist.imageUrl} alt={artist.name} fill sizes="56px" className="object-cover" />
            ) : (
              <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
            )}
          </span>
          <div className="space-y-0.5">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-rose-600 dark:text-rose-300">
              {copy.ui.seeArtist}
            </p>
            <h2 className="text-2xl font-black tracking-tight text-foreground sm:text-3xl">{artist.name}</h2>
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
              {artist.recentArticleCount} {copy.ui.inNewsRecently}
            </p>
          </div>
        </div>
        <Link
          href={withLanguagePrefix(`/artists/${artist.slug}`, language)}
          className="shrink-0 text-[11px] font-mono uppercase tracking-[0.16em] text-rose-600 hover:text-rose-500 dark:text-rose-300"
        >
          {copy.ui.seeAll} →
        </Link>
      </header>
      <HorizontalShelf>
        {artist.clusters.map((cluster) => (
          <SampleCard
            key={cluster.id}
            cluster={cluster}
            language={language}
            copy={copy}
            imageFailed={failedImages.has(cluster.id)}
            onImageFail={() => onImageFail(cluster.id)}
          />
        ))}
      </HorizontalShelf>
    </section>
  );
}

function GenreSpotlightShelf({
  genre,
  language,
  copy,
  failedImages,
  onImageFail,
}: {
  genre: GenreSpotlightRow;
  language: SiteLanguage;
  copy: SiteCopy;
  failedImages: Set<string>;
  onImageFail: (id: string) => void;
}) {
  return (
    <section className="space-y-4">
      <header className="flex items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex size-14 items-center justify-center rounded-full border border-border">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-rose-600 dark:text-rose-300">
              {genre.name.slice(0, 3)}
            </span>
          </span>
          <div className="space-y-0.5">
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-rose-600 dark:text-rose-300">
              {copy.ui.seeGenre}
            </p>
            <h2 className="text-2xl font-black capitalize tracking-tight text-foreground sm:text-3xl">{genre.name}</h2>
            <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
              {copy.ui.storiesCount.replace("{count}", String(genre.recentClusterCount))}
            </p>
          </div>
        </div>
        <Link
          href={withLanguagePrefix(`/genres/${genre.slug}`, language)}
          className="shrink-0 text-[11px] font-mono uppercase tracking-[0.16em] text-rose-600 hover:text-rose-500 dark:text-rose-300"
        >
          {copy.ui.seeAll} →
        </Link>
      </header>
      <HorizontalShelf>
        {genre.clusters.map((cluster) => (
          <SampleCard
            key={cluster.id}
            cluster={cluster}
            language={language}
            copy={copy}
            imageFailed={failedImages.has(cluster.id)}
            onImageFail={() => onImageFail(cluster.id)}
          />
        ))}
      </HorizontalShelf>
    </section>
  );
}

function SampleCard({
  cluster,
  language,
  copy,
  imageFailed,
  onImageFail,
}: {
  cluster: ArtistSpotlightRow["clusters"][number];
  language: SiteLanguage;
  copy: SiteCopy;
  imageFailed: boolean;
  onImageFail: () => void;
}) {
  const translation =
    cluster.translations.find((t) => t.language === language) ?? cluster.translations[0];
  return (
    <Link
      href={withLanguagePrefix(`/clusters/${cluster.id}`, language)}
      className="group flex min-w-[260px] max-w-[300px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl border border-border bg-card/50 transition hover:border-rose-400/40"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
        {/* `hasImage` now mirrors the image pipeline's full fallback
            chain (article → band Spotify art), so it's authoritative.
            The onError handler is kept as defense-in-depth for dead
            Spotify URLs that still 404 at request time. */}
        {cluster.hasImage && !imageFailed ? (
          <Image
            src={imageApiUrl("cluster", cluster.id)}
            alt={translation?.title ?? "Story"}
            fill
            sizes="300px"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.04]"

            onError={onImageFail}
          />
        ) : (
          <div className="aurora-fallback absolute inset-0" aria-hidden="true" />
        )}
      </div>
      <div className="flex flex-col gap-1.5 p-3">
        <p className="text-sm font-bold leading-snug text-foreground line-clamp-2 group-hover:text-rose-600 dark:group-hover:text-rose-300">
          {translation?.title ?? copy.ui.untitledStory}
        </p>
        <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-muted-foreground">
          {cluster.sourceNames.slice(0, 2).join(" · ")}
        </p>
      </div>
    </Link>
  );
}
